import type { Destination, Transport } from '../types';
import { matchesCity } from './placeNames';
import { formatMinutes } from './journeys';
import { transportDuringVisit } from './destinationItems';
import { compareScheduleTime, scheduleMinute } from './scheduleTimezones';

/** Only logistical facts go to the generator; never booking documents or money. */
export function stayTransportContext(visit: Destination, transports: Transport[]) {
  return transports.filter(t => transportDuringVisit(t, visit, transports)).map(t => ({
    id: t.id || t.flightNum || String(t.dep), kind: t.isRental ? 'rental' as const : 'journey' as const,
    mode: t.mode || 'Other', from: t.from || '', to: t.to || '', dep: t.dep || '', arr: t.arr || '',
  }));
}

export function boundaryTransport(destination: Destination, transports: (Transport & { _dayDate?: string })[], direction: 'arrival' | 'departure') {
  const arrival = direction === 'arrival';
  return transports.filter(t => {
    if (t.isRental || transportDuringVisit(t, destination, transports)) return false;
    const boundary = arrival ? destination.arrivalDate : destination.departureDate;
    const place = arrival ? t.toCity || t.to : t.fromCity || t.from;
    // A leg records a terminal, a destination records a city. Comparing the
    // two as plain strings meant a flight into "Tokyo Narita" belonged
    // nowhere unless the parser had filled toCity — and the leg then dropped
    // out of the generated schedule altogether.
    if (!matchesCity(place, destination.name)) return false;
    // A leg is matched by the end that faces this destination. For a
    // departure that's normally `dep` — except on an overnight leg, where
    // boarding happens the night before the destination's own departure date
    // (touching destinations share one date, so `dep` lands on the previous
    // day). Its `arr` still falls on the boundary, and the city filter above
    // has already established this leg leaves *from here*, so accepting
    // either end can't pull in an unrelated journey.
    if (arrival && t.arr?.slice(0, 10) === boundary) return true;
    if (!arrival && (t.dep?.slice(0, 10) === boundary || t.arr?.slice(0, 10) === boundary)) return true;
    // A manually entered leg may have no times or dates, but its owning
    // itinerary day still establishes which boundary it belongs to.
    return !t.dep && !t.arr && t._dayDate === boundary;
  }).sort((a, b) => {
    const usable = (t: Transport) => Number(!!t.mode && /T\d{2}:\d{2}/.test(arrival ? t.arr : t.dep));
    return usable(b) - usable(a) || (arrival ? b.arr.localeCompare(a.arr) : a.dep.localeCompare(b.dep));
  })[0];
}

export type TravelEstimate = { date: string; slot: {
  id: string; type: 'travel'; title: string; start: string; durationMin: number;
  estimatedTravel: true; journey: true; locked: false; subtitle: string; legKind: string;
} };

function savedDurationMinutes(value?: string): number | null {
  if (!value) return null;
  const hours = /\b(\d+)\s*(?:h|hr|hour)s?\b/i.exec(value);
  const minutes = /\b(\d+)\s*(?:m|min|minute)s?\b/i.exec(value);
  return hours || minutes ? Number(hours?.[1] || 0) * 60 + Number(minutes?.[1] || 0) : null;
}

export function travelEstimates(destination: Destination, previous: Destination | undefined, next: Destination | undefined, transports: Transport[], routedMinutes?: (transport: Transport) => number | null): TravelEstimate[] {
  return (['arrival', 'departure'] as const).flatMap(direction => {
    const arrival = direction === 'arrival';
    const booking = boundaryTransport(destination, transports, direction);
    if (booking?.mode && /T\d{2}:\d{2}/.test((arrival ? booking.arr : booking.dep) || '')) return [];
    const date = arrival ? destination.arrivalDate : destination.departureDate;
    if (!date) return [];
    const adjacentStop = arrival ? previous : next;
    const durationMin = booking && (savedDurationMinutes(booking.duration) || routedMinutes?.(booking));
    const mode = booking?.mode?.toLowerCase();
    const legKind = mode === 'car' || mode === 'taxi' ? 'car' : mode === 'boat' ? 'ferry' : mode || 'other';
    const from = booking?.fromCity || booking?.from;
    const to = booking?.toCity || booking?.to;
    const knownJourney = !!(booking?.mode && from && to);
    const title = knownJourney
      ? `${mode === 'car' ? 'Drive' : mode === 'taxi' ? 'Taxi' : booking!.mode} from ${from} to ${to}`
      : `Destination ${direction} details unknown`;
    const guidance = booking
      ? booking.mode ? `Add the ${direction} time to your transport in Itinerary`
        : `Complete your ${direction} transport details in Itinerary`
      : adjacentStop
        ? `Add how you’re travelling ${arrival ? 'from' : 'to'} ${adjacentStop.name} in Itinerary`
        : `If you’re travelling that day, add your ${direction} transport in Itinerary`;
    // Unknown start stays provisional; known route duration replaces the generic allowance.
    return [{ date, slot: {
      id: `estimated-${direction}-${date}`, type: 'travel' as const,
      title, start: arrival ? '09:00' : '15:00', durationMin: durationMin || 180,
      estimatedTravel: true as const, journey: true as const, locked: false as const, legKind,
      subtitle: `${durationMin ? `About ${formatMinutes(durationMin)}. ` : 'Estimated travel allowance · 3 hours. '}${guidance}, then refresh your schedule.`,
    } }];
  });
}

/** Derived journey details; only a user-chosen provisional start may be saved. */
export function withTravelEstimates(schedule: any, estimates: TravelEstimate[]): any {
  const days = (schedule?.days || []).map((day: any) => ({ ...day, slots: (day.slots || []).filter((s: any) =>
    !s.estimatedTravel && !estimates.some(e => e.date === day.date && s.placeholder && s.type === 'travel'
      && s.title?.toLowerCase().startsWith(e.slot.id.includes('arrival') ? 'arrive' : 'depart')),
  ) }));
  for (const estimate of estimates) {
    let day = days.find((d: any) => d.date === estimate.date);
    if (!day) { day = { date: estimate.date, title: '', slots: [] }; days.push(day); }
    const saved = schedule?.days?.find((d: any) => d.date === estimate.date)?.slots?.find((s: any) => s.id === estimate.slot.id && s.startOverride);
    day.slots.push(saved ? { ...estimate.slot, start: saved.start, startOverride: true } : estimate.slot);
    day.slots.sort(compareScheduleTime);
  }
  return { ...schedule, days: days.sort((a: any, b: any) => a.date.localeCompare(b.date)) };
}

/** Keep an edited provisional time through any schedule action, including reorder/cascade. */
export function preserveEstimateStartEdits(next: any, before: any): any {
  const starts = new Map<string, string>((before?.days || []).flatMap((day: any) =>
    (day.slots || []).filter((slot: any) => slot.estimatedTravel).map((slot: any) => [slot.id, slot.start])));
  return { ...next, days: (next.days || []).map((day: any) => ({ ...day,
    slots: (day.slots || []).map((slot: any) => slot.estimatedTravel && starts.has(slot.id) && starts.get(slot.id) !== slot.start
      ? { ...slot, startOverride: true } : slot),
  })) };
}

/** Only AI additions are filtered. Existing user plans remain untouched. */
export function reserveEstimatedTravel<T extends { start?: string; durationMin?: number }>(slots: T[], estimates: TravelEstimate['slot'][]): T[] {
  const minutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
  return slots.filter(slot => {
    if (!slot.start) return estimates.length === 0;
    const start = minutes(slot.start);
    const end = start + Math.max(1, slot.durationMin ?? 60);
    return !estimates.some(estimate => {
      const timed = slot as T & { clock?: { date: string; zone: string; day: string } };
      const anchor = estimate as typeof estimate & { clock?: { date: string; zone: string; day: string } };
      if (timed.clock && anchor.clock) {
        const at = scheduleMinute(timed), fixed = scheduleMinute(anchor);
        return at < fixed + estimate.durationMin && at + Math.max(1, slot.durationMin ?? 60) > fixed;
      }
      return start < minutes(estimate.start) + estimate.durationMin && end > minutes(estimate.start);
    });
  });
}

/**
 * Whether a leg should go by public transport or private (taxi/drive), given
 * the traveller's stated preference and both durations.
 *
 * Previously hardcoded: transit won unless it was more than 50% slower than
 * driving. That's a reasonable default but it isn't everyone's — someone who
 * would rather not deal with buses at all, and someone optimising purely for
 * time, both got the same answer.
 *
 * `null` for either duration means that mode couldn't be routed at all.
 */
export function chooseTransitOrDriving(
  transitMin: number | null,
  drivingMin: number | null,
  pref: 'avoid-public' | 'prefer-public' | 'fastest',
): 'transit' | 'driving' | null {
  if (transitMin == null && drivingMin == null) return null;
  if (transitMin == null) return 'driving';
  if (drivingMin == null) return 'transit';

  switch (pref) {
    case 'avoid-public':
      // Only fall back to public transport when driving is dramatically worse
      // — usually a sign the route genuinely isn't drivable (pedestrian zones,
      // islands, closed roads) rather than a preference to override.
      return drivingMin <= transitMin * 2 ? 'driving' : 'transit';

    case 'prefer-public':
      // Stay on public transport unless it's more than twice the time, i.e.
      // the difference has stopped being a matter of preference.
      return transitMin <= drivingMin * 2 ? 'transit' : 'driving';

    case 'fastest':
    default:
      // "Materially shorter": private has to save at least 25% before it's
      // worth the cost and the hassle of arranging it. A dead heat goes to
      // public transport, which is the cheaper default.
      return drivingMin <= transitMin * 0.75 ? 'driving' : 'transit';
  }
}
